import React from 'react';
import { NavConfigItem, RouteParameterMap } from '.';
//icons
import CodeIcon from '@mui/icons-material/Code';
import SettingsApplicationsIcon from '@mui/icons-material/SettingsApplications';
import { Permission } from 'Models/Templates/Permission/Permission';
import DeveloperBoardIcon from '@mui/icons-material/DeveloperBoard';
import SettingsEthernetIcon from '@mui/icons-material/SettingsEthernet';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import ArticleIcon from '@mui/icons-material/Article';
import PagesIcon from '@mui/icons-material/Pages';
import GridOnIcon from '@mui/icons-material/GridOn';
import BuildIcon from '@mui/icons-material/Build';
import ShareIcon from '@mui/icons-material/Share';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import ReceiptIcon from '@mui/icons-material/Receipt';

// FeatureFlags enum is unavailable at module init due to a circular dependency through
// FeatureFlagProvider → Components/Navigation → Navigation → Routes → here.
// checkFlag reads window/process.env directly to resolve routes before that cycle is broken.
function checkFlag(flag: string): boolean {
  const windowVal = (window as Record<string, any>)[flag];
  if (windowVal != null && !String(windowVal).startsWith('#{')) {
    return String(windowVal).toLowerCase() === 'true';
  }
  return process.env[`REACT_APP_${flag}`] === 'true';
}

const v3ReviewerProfileEnabled = checkFlag('V3_REVIEWER_PROFILE');

export enum ROUTES {
  CLIENT_DASHBOARD = '/Home',
  DASHBOARD = '/Dashboard',
  SWAGGER = '/Swagger',
  API_SWAGGER = '/ApiSwagger',
  ELMAH = '/Elmah',
  UNAUTHORIZED = '/Unauthorized',
  END_SESSION = '/EndSession',
  USER_MANAGEMENT = '/UserManagement',
  ENUMERATIONS = '/Enumerations',
  EXAMPLES = '/Examples',
  QUICK_SEARCH = '/QuickSearch',
  INTEGRATIONS = '/Integrations',
  ADD_EDIT = '/AddEdit',
  CASE_ADD_EDIT = '/CaseAddEdit',
  DETAILED_SEARCH = '/DetailedSearch',
  VIEW = '/View',
  REFERRAL = '/Referral',
  REFERRALV3 = '/Referral?v=3',
  REFERRALCOMMERCIAL = '/ReferralCommercial',
  CLAIMANT = '/Claimant',
  CASE_MANAGEMENT = '/CaseManagement',
  OPEN_CASES = '/CaseManagement/OpenCases',
  CLOSED_CASES = '/CaseManagement/ClosedCases',
  APPOINTMENT = '/Appointment',
  CLAIM = '/Claim',
  GUIDELINE = '/Guideline',
  REFERRALSREQUEST = '/ReferralsRequest',
  REFERRALREQUESTPROPOSALS = '/ReferralRequestProposals',
  REFERRALREQUESTPROPOSALSCOMMERCIAL = '/ReferralRequestProposalsCommercial',
  ENTITIES = '/Entities',
  DOCUMENTS = '/Documents',
  REFERRALS = '/Referrals',
  CONFIGURATION = '/Configuration',
  REVIEWER_PANEL = '/ReviewerPanel',
  REVIEWER_PROFILE = '/Reviewers',
  // Hidden admin-only page (STRAD2-2591) - intentionally not linked from any menu.
  REVIEWER_IMPORT = '/ReviewerImport',
  CLAIM_ESCALATIONS = '/ClaimEscalations',
  REVIEWER = '/Reviewer',
  REVIEWER_CONVERT = '/ReviewerConvert',
  QA = '/QA',
  AUDIT_QUEUE = '/AuditQueue',
  ADD_AUDIT_REPORT = '/AddAuditReport',
  ESCALATIONS_EXPORT = '/EscalationsExport',
  INVOICES = '/Invoices',
  MY_INVOICES = '/MyInvoices',
  COMMERCIAL = '/Commercial',
  COMPONENT_EXAMPLES = '/ComponentExamples',
  REPORTS = '/Reports',
  COMPLIANCE = '/Compliance',
  // Hidden admin-only page — reached only from the "V3 Roles" grid on UserManagementRoles.
  V3_ROLE = '/V3Role',
}

export const ROUTE_PARAMETERS: RouteParameterMap = {
  [ROUTES.CASE_ADD_EDIT]: ':caseId',
  [ROUTES.REFERRAL]: ':entityId',
  [ROUTES.REFERRALCOMMERCIAL]: ':entityId',
  [ROUTES.CLAIMANT]: ':entityId',
  [ROUTES.CLAIM]: ':entityId',
  [ROUTES.APPOINTMENT]: ':entityId',
  [ROUTES.GUIDELINE]: ':entityId',
  [ROUTES.REFERRALSREQUEST]: ':entityId',
  [ROUTES.CLAIM_ESCALATIONS]: ':entityId',
};

export const MAIN_NAV_CONFIG: NavConfigItem[] = [
  {
    label: 'Home',
    route: ROUTES.CLIENT_DASHBOARD,
    default: /* only if user has permissions */ false,
    permissions: [Permission.ViewClientDashboard],
    icon: <ShowChartIcon />,
  },
  {
    label: 'Dashboard',
    route: ROUTES.DASHBOARD,
    default: true,
    permissions: [Permission.ViewDashboard],
    icon: <ShowChartIcon />,
  },
  {
    label: 'Referrals',
    route: ROUTES.REFERRALS,
    permissions: [
      Permission.ViewOpenCases,
      Permission.ViewClosedCases,
      Permission.ViewReviewedReferrals,
      Permission.ViewAssignedToWriters,
      Permission.ViewNewReferrals,
      Permission.ViewPendingAppointments,
      Permission.ViewAllClaimants,
    ],
    default: false,
    icon: <GridOnIcon />,
  },

  {
    label: 'Reviewer Panel',
    route: v3ReviewerProfileEnabled
      ? ROUTES.REVIEWER_PROFILE
      : ROUTES.REVIEWER_PANEL,
    default: false,
    permissions: [Permission.ViewReviewers],
    icon: <ShareIcon />,
  },
  {
    label: 'Reports',
    route: ROUTES.REPORTS,
    default: false,
    permissions: [Permission.ViewInvoices],
    icon: <ReceiptIcon />,
  },
  {
    label: 'My Invoices',
    route: ROUTES.MY_INVOICES,
    default: false,
    permissions: [Permission.ViewMyInvoices],
    icon: <ReceiptIcon />,
  },
  {
    label: 'Compliance',
    route: ROUTES.COMPLIANCE,
    default: false,
    permissions: [
      Permission.ViewEscalationsExport,
      Permission.ViewAuditQueue,
      Permission.ViewAuditReport,
    ],
    icon: <PagesIcon />,
  },
  {
    label: 'Documents',
    route: ROUTES.DOCUMENTS,
    default: false,
    permissions: [Permission.ViewGuidelines],
    icon: <ArticleIcon />,
  },
];

export const SETTINGS_NAV_CONFIG: NavConfigItem[] = [
  {
    label: 'User Management',
    icon: <PeopleOutlineIcon />,
    route: ROUTES.USER_MANAGEMENT,
    permissions: [Permission.AddUser, Permission.EditUser],
  },
  {
    label: 'Configuration',
    icon: <BuildIcon />,
    route: ROUTES.CONFIGURATION,
    permissions: [
      Permission.AddEvalTypes,
      Permission.EditEvalTypes,
      Permission.AddClient,
      Permission.EditClient,
      Permission.AddPhysician,
      Permission.EditPhysician,
    ],
  },
  {
    label: 'Development API',
    icon: <CodeIcon />,
    route: ROUTES.SWAGGER,
    permissions: [Permission.ViewApiKeys],
  },
  {
    label: 'Integration Swagger',
    icon: <SettingsEthernetIcon />,
    route: ROUTES.API_SWAGGER,
    permissions: [Permission.ViewApiKeys],
  },
  {
    label: 'Elmah',
    icon: <ErrorOutlineIcon />,
    route: ROUTES.ELMAH,
    permissions: [Permission.ViewErrorLog],
  },
  {
    label: 'Examples',
    icon: <DeveloperBoardIcon />,
    route: ROUTES.EXAMPLES,
    permissions: [Permission.ViewApiKeys],
  },
];
